import { useAuth0 } from "@auth0/auth0-react";
import { useContext, useState } from "react";
import { Alert, Badge, Button, Form, Table } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import type { PositionSwapStatus } from "../../../api/src/position-swaps/position-swap";
import type { TradeSide } from "../../../api/src/rebalances/rebalances.service";
import Loading from "../Loading";
import { NotificationContext } from "../Notifications";
import Content from "../components/Content";
import { refusalSentence } from "../lib/api";
import { formatCurrency } from "../lib/numbers";
import { securityPath } from "../lib/security";
import { useQuerySecurityDetails } from "../securities/lib";
import { useQueryRebalance } from "./lib";
import {
  type PositionSwapData,
  useApprovePositionSwap,
  useCancelHouseholdOtherProposals,
  useCancelOtherProposals,
  useCancelPositionSwap,
  useChangePositionSwapTrade,
  useQueryPositionSwap,
  useRebuildPositionSwap,
  useTakeOverPositionSwap,
} from "./positionSwapsLib";

export const STATUS_VARIANTS: Record<PositionSwapStatus, string> = {
  draft: "secondary",
  approved: "primary",
  executing: "info",
  executed: "success",
  cancelled: "dark",
};

/** A draft or an approved swap can be cancelled (`canCancel` in the api). */
const cancellable = (status: PositionSwapStatus) =>
  status === "draft" || status === "approved";

type HouseholdGroup = {
  key: string;
  householdId: number | null;
  name: string;
  accounts: PositionSwapData["accounts"];
};

/** The swap's accounts grouped by household, in the order the api sends. */
export function groupByHousehold(
  accounts: PositionSwapData["accounts"],
): HouseholdGroup[] {
  const groups = new Map<string, HouseholdGroup>();
  for (const account of accounts) {
    const key = String(account.householdId ?? "none");
    const group = groups.get(key) ?? {
      key,
      householdId: account.householdId,
      name: account.householdName ?? "No household",
      accounts: [],
    };
    group.accounts.push(account);
    groups.set(key, group);
  }
  return [...groups.values()];
}

const accountLabel = (account: PositionSwapData["accounts"][number]) =>
  account.displayNumber
    ? `${account.name} (${account.displayNumber})`
    : account.name;

const SIDE_LABELS: Record<TradeSide, string> = {
  buy: "Buy",
  sell: "Sell",
  sell_all: "Sell all",
};

type NotifyError = (header: string) => (error: Error) => void;

/**
 * One row of a household proposal of a draft (swap R45). The editor sets a
 * new quantity, removes the row, or restores a removed one; the api refuses
 * a sell above the units held (R94) and says why.
 */
const TradeRow = ({
  swapId,
  trade,
  accountName,
  editable,
  notifyError,
}: {
  swapId: number;
  trade: PositionSwapData["trades"][number];
  accountName: string;
  editable: boolean;
  notifyError: NotifyError;
}) => {
  const changeMutation = useChangePositionSwapTrade(swapId, trade.tradeId);
  const [amount, setAmount] = useState(String(trade.amount));
  const value = Number(amount);
  const changed =
    amount.trim() !== "" && Number.isFinite(value) && value !== trade.amount;
  const busy = changeMutation.isPending;
  const label = `${accountName} ${SIDE_LABELS[trade.side]} ${trade.symbol}`;

  return (
    <tr className={trade.removed ? "text-decoration-line-through" : undefined}>
      <td>{accountName}</td>
      <td>{SIDE_LABELS[trade.side]}</td>
      <td>{trade.symbol}</td>
      <td className="text-end">
        {editable && !trade.removed ? (
          <Form.Control
            size="sm"
            type="number"
            min={0}
            className="text-end"
            aria-label={`Units of ${label}`}
            value={amount}
            disabled={busy}
            onChange={(e) => setAmount(e.target.value)}
          />
        ) : (
          trade.amount.toLocaleString()
        )}
      </td>
      <td>{trade.removed ? "Removed" : ""}</td>
      {editable && (
        <td className="text-end text-nowrap">
          {trade.removed ? (
            <Button
              size="sm"
              variant="outline-secondary"
              aria-label={`Restore ${label}`}
              disabled={busy}
              onClick={() =>
                changeMutation.mutate(
                  { removed: false },
                  { onError: notifyError("Restore failed") },
                )
              }
            >
              Restore
            </Button>
          ) : (
            <div className="d-flex gap-2 justify-content-end">
              <Button
                size="sm"
                variant="outline-primary"
                aria-label={`Save units of ${label}`}
                disabled={!changed || busy}
                onClick={() =>
                  changeMutation.mutate(
                    { amount: value },
                    {
                      onError: (error) => {
                        setAmount(String(trade.amount));
                        notifyError("Change failed")(error);
                      },
                    },
                  )
                }
              >
                Save
              </Button>
              <Button
                size="sm"
                variant="outline-danger"
                aria-label={`Remove ${label}`}
                disabled={busy}
                onClick={() =>
                  changeMutation.mutate(
                    { removed: true },
                    { onError: notifyError("Remove failed") },
                  )
                }
              >
                Remove
              </Button>
            </div>
          )}
        </td>
      )}
    </tr>
  );
};

/** The editor's cancel of the other open proposals of one household (swap R49). */
const CancelHouseholdOtherProposals = ({
  swapId,
  householdId,
  householdName,
  notifyError,
}: {
  swapId: number;
  householdId: number;
  householdName: string;
  notifyError: NotifyError;
}) => {
  const cancelMutation = useCancelHouseholdOtherProposals(swapId, householdId);
  return (
    <Button
      size="sm"
      variant="outline-danger"
      aria-label={`Cancel the other open proposals of ${householdName}`}
      disabled={cancelMutation.isPending}
      onClick={() =>
        cancelMutation.mutate(
          {},
          { onError: notifyError("Cancel of the other proposals failed") },
        )
      }
    >
      Cancel other proposals
    </Button>
  );
};

/**
 * The trades of each account of one proposal of an executed swap (swap R102),
 * as `GET /v1/rebalances/:id?trades=true` answers them.
 */
const ExecutedTrades = ({
  proposal,
}: {
  proposal: PositionSwapData["rebalances"][number];
}) => {
  const { data, isLoading, isError } = useQueryRebalance(proposal.rebalanceId, {
    includeTrades: true,
  });
  const accounts = data?.accounts.filter((a) => a.trades.length > 0) ?? [];

  return (
    <section
      aria-label={`Executed trades of ${proposal.householdName}`}
      className="mb-3"
    >
      <h4 className="h6 d-flex gap-2 align-items-baseline">
        {proposal.householdName}
        <Link className="small" to={`/rebalances/${proposal.rebalanceId}`}>
          Proposal: {proposal.status}
        </Link>
      </h4>
      {isLoading ? (
        <Loading />
      ) : isError || !data ? (
        <p className="text-danger mb-0">
          The trades of proposal {proposal.rebalanceId} can't be read.
        </p>
      ) : accounts.length === 0 ? (
        <p className="text-secondary mb-0">The proposal has no trades.</p>
      ) : (
        <Table size="sm" className="mb-0">
          <thead>
            <tr>
              <th>Account</th>
              <th>Side</th>
              <th>Symbol</th>
              <th className="text-end">Units</th>
              <th className="text-end">Fill price</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {accounts.flatMap((account) =>
              account.trades.map((trade) => (
                <tr key={trade.id}>
                  <td>{account.accountName}</td>
                  <td>{SIDE_LABELS[trade.side]}</td>
                  <td>{trade.symbol}</td>
                  <td className="text-end">{trade.amount.toLocaleString()}</td>
                  <td className="text-end">
                    {formatCurrency(trade.fillPrice, 2)}
                  </td>
                  <td>{trade.status}</td>
                </tr>
              )),
            )}
          </tbody>
        </Table>
      )}
    </section>
  );
};

/**
 * The accounts of the firm that still hold the old security of an executed
 * swap (swap R83), from `GET /firm/securities/:symbol/details`, with the
 * accounts of the swap marked.
 */
const RemainingHolders = ({ swap }: { swap: PositionSwapData }) => {
  const { data, isLoading, isError } = useQuerySecurityDetails(swap.symbol);
  const inSwap = new Set(swap.accounts.map((a) => a.accountId));

  return (
    <Content>
      <h3 className="h6">Accounts still holding {swap.symbol}</h3>
      {isLoading ? (
        <Loading />
      ) : isError || !data ? (
        <p className="text-danger mb-0">
          The holders of {swap.symbol} can't be read.
        </p>
      ) : data.holdings.length === 0 ? (
        <p className="text-secondary mb-0">No account holds {swap.symbol}.</p>
      ) : (
        <Table size="sm" className="mb-0" aria-label="Remaining holders">
          <thead>
            <tr>
              <th>Account</th>
              <th>Household</th>
              <th className="text-end">Units</th>
              <th>In this swap</th>
            </tr>
          </thead>
          <tbody>
            {data.holdings.map((holding) => (
              <tr key={holding.accountId}>
                <td>
                  {holding.accountName} ({holding.accountNumber})
                </td>
                <td>{holding.householdName ?? "No household"}</td>
                <td className="text-end">{holding.units.toLocaleString()}</td>
                <td>{inSwap.has(holding.accountId) ? "Yes" : ""}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Content>
  );
};

/**
 * One position swap (APP-1486): its accounts by household, its stale accounts
 * and approval blockers while a draft, the approve and cancel controls, the
 * draft changes (take over, row changes, rebuild and the cancel of other
 * open proposals, swap R43 to R50), and once executed the trades of each
 * account and the remaining holders.
 */
const PositionSwap = () => {
  const { swapId } = useParams();
  const id = Number(swapId);
  const { data, isLoading, isError } = useQueryPositionSwap(id);

  if (isLoading) return <Loading />;
  if (isError || !data) {
    return <Alert variant="danger">Position swap {swapId} not found.</Alert>;
  }
  return <SwapView swap={data} />;
};

const SwapView = ({ swap }: { swap: PositionSwapData }) => {
  const { pushNotification } = useContext(NotificationContext);
  const { user } = useAuth0();
  const approveMutation = useApprovePositionSwap(swap.id);
  const cancelMutation = useCancelPositionSwap(swap.id);
  const takeOverMutation = useTakeOverPositionSwap(swap.id);
  const rebuildMutation = useRebuildPositionSwap(swap.id);
  const cancelOthersMutation = useCancelOtherProposals(swap.id);
  const notifyError: NotifyError = (header) => (error) =>
    pushNotification({
      id: "position-swap-error",
      header,
      body: refusalSentence(error) ?? "Something went wrong; try again.",
      variant: "danger",
    });

  const isDraft = swap.status === "draft";
  const isExecuted = swap.status === "executed";
  const blocked = swap.approvalBlockerSentences.length > 0;
  // The api decides who may change a draft (R43); this only hides the
  // controls it would refuse.
  const isEditor =
    isDraft && swap.editor !== null && swap.editor.userId === user?.sub;
  const busy =
    approveMutation.isPending ||
    cancelMutation.isPending ||
    takeOverMutation.isPending ||
    rebuildMutation.isPending ||
    cancelOthersMutation.isPending;
  const groups = groupByHousehold(swap.accounts);
  const stale = new Set(swap.staleAccounts);
  const staleAccounts = swap.accounts.filter((a) => stale.has(a.accountId));
  const conflicting = new Set(swap.conflictingHouseholds);
  const proposalOf = new Map(swap.rebalances.map((r) => [r.householdId, r]));
  const accountNames = new Map(
    swap.accounts.map((a) => [a.accountId, accountLabel(a)]),
  );

  return (
    <div className="d-grid gap-3">
      <Content>
        <div className="d-flex flex-wrap align-items-baseline gap-3">
          <h2 className="h4 mb-0">{swap.name}</h2>
          <Badge bg={STATUS_VARIANTS[swap.status]} data-testid="swap-status">
            {swap.status}
          </Badge>
        </div>
        <p className="text-secondary small mt-2 mb-0">
          Moves out of <Link to={securityPath(swap.symbol)}>{swap.symbol}</Link>{" "}
          into {swap.replacementSymbol ?? "cash"}. Built from the{" "}
          {swap.positionsDate} positions by {swap.createdBy}.
        </p>
        {isDraft && (
          <p className="small mt-1 mb-0" data-testid="swap-editor">
            {swap.editor
              ? `Editor: ${isEditor ? "you" : swap.editor.username}.`
              : "No editor."}{" "}
            {!isEditor && "Take over the draft to change it."}
          </p>
        )}
        <div className="d-flex gap-2 mt-3">
          {isDraft && !isEditor && (
            <Button
              variant="outline-primary"
              disabled={busy}
              onClick={() =>
                takeOverMutation.mutate(
                  {},
                  { onError: notifyError("Take over failed") },
                )
              }
            >
              Take over
            </Button>
          )}
          {isDraft && (
            <Button
              variant="primary"
              disabled={blocked || busy}
              onClick={() =>
                approveMutation.mutate(
                  {},
                  { onError: notifyError("Approval failed") },
                )
              }
            >
              Approve
            </Button>
          )}
          {cancellable(swap.status) && (
            <Button
              variant="outline-danger"
              disabled={busy}
              onClick={() =>
                cancelMutation.mutate(
                  {},
                  { onError: notifyError("Cancel failed") },
                )
              }
            >
              Cancel swap
            </Button>
          )}
        </div>
      </Content>

      {isDraft && blocked && (
        <Alert variant="warning" className="mb-0">
          <Alert.Heading as="h3" className="h6">
            Can't approve yet
          </Alert.Heading>
          <ul className="mb-0" aria-label="Approval blockers">
            {swap.approvalBlockerSentences.map((sentence) => (
              <li key={sentence}>{sentence}</li>
            ))}
          </ul>
          {isEditor && swap.conflictingHouseholds.length > 0 && (
            <Button
              size="sm"
              variant="outline-danger"
              className="mt-2"
              disabled={busy}
              onClick={() =>
                cancelOthersMutation.mutate(
                  {},
                  {
                    onError: notifyError(
                      "Cancel of the other proposals failed",
                    ),
                  },
                )
              }
            >
              Cancel the other open proposals of each household
            </Button>
          )}
        </Alert>
      )}

      {isDraft && staleAccounts.length > 0 && (
        <Content>
          <h3 className="h6">Stale accounts</h3>
          <p className="text-secondary small">
            These accounts have newer positions than the {swap.positionsDate}{" "}
            positions the swap was built from.
          </p>
          <ul className="mb-0" aria-label="Stale accounts">
            {staleAccounts.map((account) => (
              <li key={account.accountId}>
                {accountLabel(account)}
                {account.householdName && ` — ${account.householdName}`}
              </li>
            ))}
          </ul>
          {isEditor && (
            <Button
              size="sm"
              variant="outline-primary"
              className="mt-2"
              disabled={busy}
              onClick={() =>
                rebuildMutation.mutate(
                  {},
                  { onError: notifyError("Rebuild failed") },
                )
              }
            >
              Rebuild stale accounts
            </Button>
          )}
        </Content>
      )}

      <Content>
        <h3 className="h6">Accounts by household</h3>
        {groups.length === 0 ? (
          <p className="text-secondary mb-0">The swap trades no accounts.</p>
        ) : (
          groups.map((group) => {
            const proposal =
              group.householdId === null
                ? undefined
                : proposalOf.get(group.householdId);
            const rows = isDraft
              ? swap.trades.filter(
                  (t) => t.rebalanceId === proposal?.rebalanceId,
                )
              : [];
            const editable = isEditor && proposal?.status === "proposed";
            return (
              <section
                key={group.key}
                aria-label={`Household ${group.name}`}
                className="mb-3"
              >
                <h4 className="h6 d-flex gap-2 align-items-baseline">
                  {group.householdId === null ? (
                    group.name
                  ) : (
                    <Link to={`/clients/households/${group.householdId}`}>
                      {group.name}
                    </Link>
                  )}
                  {proposal && (
                    <Link
                      className="small"
                      to={`/rebalances/${proposal.rebalanceId}`}
                    >
                      Proposal: {proposal.status}
                    </Link>
                  )}
                  {isEditor &&
                    group.householdId !== null &&
                    conflicting.has(group.householdId) && (
                      <CancelHouseholdOtherProposals
                        swapId={swap.id}
                        householdId={group.householdId}
                        householdName={group.name}
                        notifyError={notifyError}
                      />
                    )}
                </h4>
                <Table
                  size="sm"
                  className="mb-0"
                  aria-label={`Accounts of ${group.name}`}
                >
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Positions date</th>
                      <th>Stale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.accounts.map((account) => (
                      <tr key={account.accountId}>
                        <td>{accountLabel(account)}</td>
                        <td>{account.reportedDate}</td>
                        <td>{stale.has(account.accountId) ? "Yes" : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {rows.length > 0 && (
                  <Table
                    size="sm"
                    className="mt-2 mb-0"
                    aria-label={`Trades of ${group.name}`}
                  >
                    <thead>
                      <tr>
                        <th>Account</th>
                        <th>Side</th>
                        <th>Symbol</th>
                        <th className="text-end">Units</th>
                        <th />
                        {editable && <th />}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((trade) => (
                        <TradeRow
                          // A saved change answers the row anew.
                          key={`${trade.tradeId}-${trade.amount}-${trade.removed}`}
                          swapId={swap.id}
                          trade={trade}
                          accountName={
                            accountNames.get(trade.accountId) ??
                            String(trade.accountId)
                          }
                          editable={editable}
                          notifyError={notifyError}
                        />
                      ))}
                    </tbody>
                  </Table>
                )}
              </section>
            );
          })
        )}
      </Content>

      {isExecuted && (
        <Content>
          <h3 className="h6">Executed trades</h3>
          {swap.rebalances.length === 0 ? (
            <p className="text-secondary mb-0">The swap has no proposals.</p>
          ) : (
            swap.rebalances.map((proposal) => (
              <ExecutedTrades key={proposal.rebalanceId} proposal={proposal} />
            ))
          )}
        </Content>
      )}

      {isExecuted && <RemainingHolders swap={swap} />}
    </div>
  );
};

export default PositionSwap;
